import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Box, Typography, Container, Link as MuiLink } from "@mui/material";
import { Email, ArrowBack, SoupKitchen } from "@mui/icons-material";
import { InputField, Button, ErrorMessage } from "@/components/ui";
import { api } from "@/services/api";

/**
 * Page de réinitialisation de mot de passe
 * Basée sur la maquette password_reset_request
 */
export function PasswordResetPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetMutation = useMutation({
    mutationFn: async (email: string) => {
      // TODO: Implémenter l'endpoint backend pour la réinitialisation
      // Pour l'instant, on simule avec un appel qui n'existe pas encore
      const response = await api.post("/auth/password-reset-request", {
        email,
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccess(true);
      setError(null);
    },
    onError: (err: any) => {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Erreur lors de l'envoi de l'email";
      setError(errorMessage);
      setSuccess(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email) {
      setError("Veuillez saisir votre adresse email");
      return;
    }

    resetMutation.mutate(email);
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          bgcolor: "background.default",
          p: 2,
        }}
      >
        {/* Background décoratif */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 0,
            opacity: 0.05,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: "-100px",
              top: "-100px",
              width: "600px",
              height: "600px",
              borderRadius: "50%",
              bgcolor: "primary.main",
              filter: "blur(60px)",
            }}
          />
        </Box>

        <Container
          maxWidth="sm"
          sx={{
            position: "relative",
            zIndex: 10,
            width: "100%",
            maxWidth: "480px",
          }}
        >
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 3,
              boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
              border: "1px solid rgba(255,255,255,0.5)",
              p: 4,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: "50%",
                  bgcolor: "success.light",
                  color: "success.main",
                  width: 64,
                  height: 64,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Email sx={{ fontSize: 32 }} />
              </Box>
            </Box>

            <Typography
              variant="h2"
              sx={{ mb: 2, fontSize: "28px", fontWeight: 700 }}
            >
              Email envoyé !
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
              Nous avons envoyé un lien de réinitialisation à l&apos;adresse{" "}
              <strong>{email}</strong>. Veuillez vérifier votre boîte mail.
            </Typography>

            <Box
              component={Link}
              to="/login"
              sx={{ width: "100%", textDecoration: "none" }}
            >
              <Button variant="primary" fullWidth>
                Retour à la connexion
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      {/* Background décoratif */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
          opacity: 0.05,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: "-100px",
            top: "-100px",
            width: "600px",
            height: "600px",
            borderRadius: "50%",
            bgcolor: "primary.main",
            filter: "blur(60px)",
          }}
        />
      </Box>

      {/* Main Card */}
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          maxWidth: "480px",
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
            border: "1px solid rgba(255,255,255,0.5)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              pt: 5,
              px: 4,
              pb: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                mb: 3,
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "primary.light",
                opacity: 0.1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SoupKitchen sx={{ fontSize: 32, color: "primary.main" }} />
            </Box>

            {/* Headline */}
            <Typography
              variant="h1"
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                textAlign: "center",
                mb: 1,
                letterSpacing: "-0.02em",
              }}
            >
              Réinitialiser le mot de passe
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                pt: 1,
              }}
            >
              Saisissez l&apos;adresse e-mail associée à votre compte et nous vous
              enverrons un lien pour réinitialiser votre mot de passe.
            </Typography>
          </Box>

          {/* Form Section */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              px: 4,
              py: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {error && <ErrorMessage message={error} />}

            {/* Email Field */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  ml: 0.5,
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                Adresse e-mail
              </Typography>
              <InputField
                type="email"
                placeholder="exemple@domaine.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={<Email />}
                iconPosition="start"
                fullWidth
                required
                autoComplete="email"
              />
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              loading={resetMutation.isPending}
            >
              Envoyer le lien
            </Button>
          </Box>

          {/* Footer / Back Link */}
          <Box sx={{ px: 4, pb: 4, display: "flex", justifyContent: "center" }}>
            <MuiLink
              component={Link}
              to="/login"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "14px",
                fontWeight: 500,
                color: "text.secondary",
                textDecoration: "none",
                "&:hover": {
                  color: "primary.main",
                  "& svg": {
                    transform: "translateX(-4px)",
                  },
                },
                "& svg": {
                  transition: "transform 0.2s",
                },
              }}
            >
              <ArrowBack sx={{ fontSize: 18 }} />
              Retour à la connexion
            </MuiLink>
          </Box>
        </Box>

        {/* Copyright */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "12px" }}
          >
            © 2024 Gestion Traiteur Guyane. Tous droits réservés.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
